import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { EmptyState } from "@/components/empty-state";
import { StatusBadge } from "@/components/studio/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { getMySubmissions } from "@/lib/writer.functions";

export const Route = createFileRoute("/_authenticated/studio/submissions")({
  head: () => ({
    meta: [
      { title: "Submissions & feedback — Creator Studio — Quibblies" },
      {
        name: "description",
        content:
          "Track where each Quibblies submission sits in review and read the moderation feedback on your writing.",
      },
      { property: "og:title", content: "Submissions & feedback — Quibblies Creator Studio" },
      { property: "og:description", content: "Track your submissions and moderation feedback." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: SubmissionsPage,
});

const CONCERN_LABELS: Record<string, string> = {
  privacy: "Privacy concern",
  personal_information: "Personal information concern",
  consent: "Consent concern",
  senior_review: "Needs senior review",
};

function SubmissionsPage() {
  const fetchSubmissions = useServerFn(getMySubmissions);
  const { data, isLoading } = useQuery({
    queryKey: ["studio-submissions"],
    queryFn: () => fetchSubmissions(),
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-28 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  const rows = data ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No submissions yet"
        description="When you submit a publication for review it appears here, along with any feedback from the moderation team."
      />
    );
  }

  return (
    <ul className="space-y-3">
      {rows.map((row) => {
        const decisions = (row.moderation_decisions ?? []) as {
          id: string;
          decision: string;
          feedback: string | null;
          concerns: string[] | null;
          created_at: string;
        }[];
        return (
          <li key={row.id} className="rounded-2xl border border-border/60 bg-background/60 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-display text-lg text-foreground">
                  {row.stories?.title ?? "Untitled publication"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Submitted {new Date(row.submitted_at).toLocaleDateString()}
                  {row.reviewed_at
                    ? ` · reviewed ${new Date(row.reviewed_at).toLocaleDateString()}`
                    : " · waiting for a moderator"}
                </p>
              </div>
              <StatusBadge status={row.status} />
            </div>

            {decisions.length > 0 ? (
              <div className="mt-4 space-y-3 border-t border-border/60 pt-4">
                {decisions.map((decision) => (
                  <div key={decision.id}>
                    <p className="text-sm font-semibold text-foreground">
                      {decision.decision.replace(/_/g, " ")}
                    </p>
                    {decision.feedback ? (
                      <p className="mt-1 text-sm text-muted-foreground">{decision.feedback}</p>
                    ) : null}
                    {decision.concerns && decision.concerns.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {decision.concerns.map((concern) => (
                          <span
                            key={concern}
                            className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-amber-500"
                          >
                            {CONCERN_LABELS[concern] ?? concern}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
